import { Partnership, PartnershipTag, PartnershipModpack, PartnershipCreator } from '../../types/partnership';
import { PartnershipStatusBadge } from './PartnershipStatusBadge';
import { PartnershipPriorityBadge } from './PartnershipPriorityBadge';
import { PlayerProfile } from '../PlayerProfile';
import { cn } from '../../utils/cn';
import { formatDistanceToNow } from 'date-fns';
import { Calendar, Package, Users, User } from 'lucide-react';

interface PartnershipCardProps {
  partnership: Partnership;
  modpack?: PartnershipModpack | null;
  creator?: PartnershipCreator;
  tags?: PartnershipTag[];
  onClick: () => void;
}

export const PartnershipCard = ({
  partnership,
  modpack,
  creator,
  tags: _tags,
  onClick,
}: PartnershipCardProps) => {
  const hasModpack = !!modpack;

  return (
    <div
      onClick={onClick}
      className={cn('glass-card glass-card-interactive p-4 group')}
    >
      <div className="flex items-start gap-4">
        {modpack?.logo_url ? (
          <img
            src={modpack.logo_url}
            alt={modpack.name}
            className="w-12 h-12 rounded-lg object-cover bg-dark-900/50 flex-shrink-0 ring-1 ring-glass-200/10"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : !hasModpack && creator ? (
          <span className="icon-tile icon-tile-neutral w-12 h-12">
            <User className="w-5 h-5" />
          </span>
        ) : null}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 min-w-0">
            <h3 className="text-sm font-semibold text-white truncate">
              {hasModpack
                ? modpack.name
                : creator?.name || 'Unknown Partnership'}
            </h3>
            {hasModpack && creator && (
              <span className="text-xs text-glass-400 truncate flex items-center gap-1 flex-shrink-0">
                <Users className="w-3 h-3" />
                {creator.name}
              </span>
            )}
            {!hasModpack && (
              <span className="chip flex-shrink-0">Creator-level</span>
            )}
          </div>

          {/* Status + priority + assignee */}
          <div className="flex items-center flex-wrap gap-2 mb-2">
            <PartnershipStatusBadge status={partnership.status} />
            <PartnershipPriorityBadge priority={partnership.priority} />
            {partnership.assigned_to && (
              <div className="flex items-center gap-1 text-xs text-glass-400">
                <span>→</span>
                <PlayerProfile uuid={partnership.assigned_to} inline showUuid={false} showAvatar={true} />
              </div>
            )}
          </div>

          {/* Summary */}
          {partnership.summary && (
            <p className="text-sm text-glass-300 line-clamp-2 mb-2">
              {partnership.summary}
            </p>
          )}

          {/* Footer */}
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-glass-500 tabular-nums">
            {modpack?.platform_type && (
              <span className="chip">
                <Package className="w-3 h-3" />
                {modpack.platform_type}
              </span>
            )}
            {modpack?.player_count_estimate && (
              <span>{modpack.player_count_estimate.toLocaleString()} est. players</span>
            )}
            {partnership.start_date && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Started {formatDistanceToNow(new Date(partnership.start_date < 1e12 ? partnership.start_date * 1000 : partnership.start_date), { addSuffix: true })}
              </span>
            )}
            <span>
              Created {formatDistanceToNow(new Date(partnership.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
